/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-nested-ternary */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Modal } from '@madeeasy/universal-login';
import { format } from 'date-fns';
import Image from 'next/image';
import { getCurrentOrgName } from '../../../utils/helpers';
import CREATE_ORDER from '../../../gql/mutations/create-order.gql';
import REQUEST_TO_CALLBACK from '../../../gql/mutations/request-to-callback.gql';
import SuccessFormPopup from '../../profile/forms/SuccessFormPopup';
import { madeeasyAdmissionLink, nextiasAdmissionLink } from '../../../utils/constants';
import { ToastContext } from '../../../contexts/toast-context-with-provider';
import { useUser } from '../../../contexts/ssr-user-context';
import calender from '../../../../public/assets/clarity_date-solid.svg';
import location from '../../../../public/assets/mdi_location.svg';
import { trackEvent } from '../../../utils/analytics';
import EventNames from '../../../utils/event-name-constants';

export default function CourseCard({ course }) {
  const { user } = useUser();

  const { showToast } = useContext(ToastContext);
  const [showLogin, setShowLogin] = useState(false);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { ENROLL_NOW_CLICKED } = EventNames;

  function toggleModal() {
    setShowLogin(false);
  }

  // const isClosed = new Date(course?.course?.purchaseOptions?.[0]?.validTillDate) < new Date();
  const [createOrder] = useMutation(CREATE_ORDER);
  const [requestTocallback] = useMutation(REQUEST_TO_CALLBACK);

  const redirectionLink = getCurrentOrgName() === 'madeeasyprime' ? madeeasyAdmissionLink : nextiasAdmissionLink;

  const handleRequestCall = async () => {
    if (course?.id) {
      try {
        const response = await requestTocallback({
          variables: {
            courseId: course?.course?.id,
            batchId: course?.id,
            productId: course?.course?.purchaseOptions[0]?.id,
          },
        });
        if (response?.data?.requestCallback) {
          setPopupOpen(true);
        } else {
          setPopupOpen(false);
        }
      } catch (err) {
        console.error('Error request to callback:', err);
      }
    } else {
      showToast('Please select a batch to proceed');
    }
  };

  const handleEnrollNow = async () => {
    if (user?.id) {
      setShowLogin(false);
      if (course?.course?.purchaseOptions?.[0]?.isPurchaseAllowed) {
        trackEvent(ENROLL_NOW_CLICKED, {
          courseId: course?.course?.id,
          courseName: course?.course?.name,
          courseType: course?.course?.type,
          screenName: 'Course Landing Page',
          batchName: course?.name,
          batchId: course?.id,
        });
        try {
          const response = await createOrder({
            variables: {
              productId: course?.course?.purchaseOptions[0]?.id,
              courseId: course?.course?.id,
              batchId: course?.id,
            },
          });
          const paymentLink = response?.data?.createOrder?.paymentLink;
          if (paymentLink) {
            window.open(paymentLink, '_blank');
          }
        } catch (err) {
          console.error('Error creating order:', err);
        }
      } else {
        handleRequestCall();
      }
    } else {
      const redirect = encodeURIComponent(window.location.href);
      window.history.pushState({}, '', `?redirectTo=${redirect}`);
      setShowLogin(true);
    }
  };

  return (
    <div
      className="w-full max-w-[360px] md:max-w-[460px] bg-[#F2F2F2] rounded-[10px] shadow-[0px_4px_4px_0px_#00000040] m-2 h-full relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: '2px solid transparent',
        borderRadius: '10px',
        ...(isHovered && {
          backgroundImage:
            'linear-gradient(#F2F2F2, #F2F2F2), linear-gradient(180deg, #0B5BA3 0%, #04223D 100%)',
          backgroundOrigin: 'padding-box, border-box',
          backgroundClip: 'padding-box, border-box',
        }),
      }}
    >
      <div className="flex flex-col gap-2 p-4 sm:p-5">
        <div className="flex justify-between items-start gap-1">
          <h4 className="text-base sm:text-lg font-semibold text-black line-clamp-2" title={course?.name}>
            {course?.name}
          </h4>
          <span
            className={`text-xs sm:text-sm rounded px-2 py-1 ${
              course?.course?.type === 'Regular'
                ? 'text-[#515151] bg-[#dff4ce]'
                : 'text-[#515151] bg-[#F4CFCF]'
            }`}
          >
            {course?.course?.type}
          </span>
        </div>

        <div className="flex justify-between w-full">
          <div>
            <div className="flex items-center gap-1">
              <Image src={calender} alt="calender" width={16} height={16} />
              <p className="text-xs sm:text-sm text-[#8E8E8E] mb-0">Batch Date</p>
            </div>
            <p className="font-semibold text-[#334155] text-sm sm:text-base pl-5 mb-0">
              {new Date(course?.commencementDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
          <div>
            <p className="text-xs sm:text-sm text-[#8E8E8E] mb-0">Batch Time</p>
            <p className="font-semibold text-[#334155] text-sm sm:text-base mb-0">
              {`${format(new Date(`1970-01-01T${course?.startTime}`), 'h:mm a').toUpperCase()} - 
              ${format(new Date(`1970-01-01T${course?.endTime}`), 'h:mm a').toUpperCase()}`}
            </p>
          </div>
        </div>

        {course?.venue?.name ? (
          <div className="flex justify-between items-start w-full mb-6">
            <div>
              <div className="flex items-center">
                <Image src={location} alt="venue" width={20} height={20} />
                <p className="text-xs sm:text-sm text-[#8E8E8E] mb-0">Venue</p>
              </div>
              <p className="font-semibold text-[#334155] text-sm sm:text-base ml-5 mb-0">
                {course?.venue?.name}
              </p>
            </div>
            <div className="inline-flex flex-col items-center bg-[#DDF2FF] font-semibold px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md">
              {course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.baseCost > course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.listedCost && <span className="line-through opacity-60 mr-2">{`${course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.baseCost}`}</span>}
              {`${course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.listedCost}`}
            </div>
          </div>
        ) : (
          <div>
            {
          course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.listedCost
          && (
          <div className="inline-flex flex-col items-center bg-[#DDF2FF] font-semibold px-3 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md mb-6">
            {course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.baseCost > course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.listedCost && <p className="line-through opacity-60 mr-2">{`${course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.baseCost}`}</p>}
            <p>{`${course?.course?.purchaseOptions?.[0]?.priceDetails?.listedPriceDetails?.listedCost}`}</p>
          </div>
          )
        }
          </div>
        )}
      </div>

      <div className="px-4 sm:px-5 pb-4 absolute bottom-0 w-full">
        <button
          type="button"
          // disabled={isClosed}
          className={`py-1 text-md sm:text-lg font-bold text-white w-full rounded-lg ${
            isHovered ? 'bg-[#20B486]' : 'bg-[#0B5BA3]'
          }`}
          onClick={handleEnrollNow}
        >
          Enroll Now
        </button>
      </div>

      {isPopupOpen && (
      <SuccessFormPopup
        isOpen={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        isPageNotRedirect
        message="Our academic counsellors will contact you in some time."
        type="3"
        pageType="requestCallback"
        redirectionText="Click here to start your Admission Process !"
        redirectionLink={redirectionLink}
      />
      )}
      {showLogin ? <Modal toggleModal={toggleModal} text="Login" /> : null}
    </div>
  );
}

CourseCard.propTypes = {
  course: PropTypes.object,
};
